import { useState, useEffect, useCallback, useRef } from 'react';
import pdfCourtFormsApiService from '~/src/services/api/pdfCourtFormsApiService';
import { useCurrentOrgFprint } from '~/src/entities/user';
import { getRegion, Region } from '~/src/regions/config/utils';
import type { Document } from '~/src/services/pdfCourtFormsService';

const PAGE_SIZE = 20;

const getSearchRegions = (): string => {
  const region = getRegion();
  if (region === Region.US) return 'us,ca';
  return region;
};

const useCourtFormsSearch = (query: string) => {
  const orgFprint = useCurrentOrgFprint();
  const [results, setResults] = useState<Document[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const activeQueryRef = useRef(query);
  const pageRef = useRef(1);

  const fetchPage = useCallback(
    async (searchQuery: string, pageNum: number, reset: boolean) => {
      setLoading(true);
      try {
        const response = await pdfCourtFormsApiService.fetch(
          orgFprint,
          {
            page: pageNum,
            search: searchQuery,
            sortBy: 'region,filter_category,title',
            order: 'asc',
            limit: PAGE_SIZE,
            orgFprint,
          },
          { facets: { locality: '', region: getSearchRegions() } },
        );

        if (activeQueryRef.current !== searchQuery) return;

        const docs: Document[] = (response.hits || []).map(
          (hit: { document: Document }) => hit.document,
        );
        setResults((prev) => (reset ? docs : [...prev, ...docs]));
        setDone(docs.length < PAGE_SIZE);
      } catch (error) {
        console.error('Court forms search failed:', error);
        setDone(true);
      } finally {
        setLoading(false);
      }
    },
    [orgFprint],
  );

  useEffect(() => {
    activeQueryRef.current = query;
    pageRef.current = 1;
    setResults([]);
    setPage(1);
    setDone(false);

    if (query.length >= 3) {
      fetchPage(query, 1, true);
    }
  }, [query, fetchPage]);

  useEffect(() => {
    if (page <= 1 || page !== pageRef.current || query.length < 3) return;
    fetchPage(query, page, false);
  }, [page, query, fetchPage]);

  const loadMore = useCallback(() => {
    if (!loading && !done) {
      setPage((prev) => {
        const next = prev + 1;
        pageRef.current = next;
        return next;
      });
    }
  }, [loading, done]);

  return { results, loading, done, loadMore };
};

export default useCourtFormsSearch;
